import axios from 'axios';
import { apiClient } from './api.client';
import { parseApiError } from './api.errors';
import type { 
  Trace,
  TraceListParams, 
  TraceListResponse, 
  TraceResponse,
  TraceAggregatedMetrics
} from '@/types/traces';

/**
 * Servicio para manejar operaciones relacionadas con Traces (Llamadas)
 * Implementa todos los endpoints de la API de traces
 */
class TracesApiService {
  private getAuthHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Lista todos los traces con filtros y paginación - NUEVO ENDPOINT CON TAGS
   */
  async listTraces(
    params: TraceListParams = {}
  ): Promise<TraceListResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      // 🏷️ NUEVO: Tags es el filtro principal (reemplaza client_project_id)
      if (params.tags && params.tags.length > 0) {
        queryParams.append('tags', params.tags.join(','));
      }

      // 📄 Paginación (page-based, no offset-based)
      if (params.page) {
        queryParams.append('page', params.page.toString());
      } else {
        queryParams.append('page', '1'); // Default a página 1
      }

      if (params.limit) {
        queryParams.append('limit', Math.min(params.limit, 100).toString()); // Max 100
      } else {
        queryParams.append('limit', '20'); // Default 20
      }

      // 🔍 Filtros adicionales (orderBy removido - no soportado por el nuevo endpoint)
      const additionalFilters = [
        'sessionId', 'userId', 'name', 'fromTimestamp', 'toTimestamp', 
        'version', 'release', 'environment'
      ];
      
      additionalFilters.forEach(key => {
        const value = params[key as keyof typeof params];
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              queryParams.append(key, value.join(','));
            }
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      console.log('TracesService: Fetching traces with new endpoint:', `/traces?${queryParams.toString()}`);

      const response = await apiClient.get(`/traces?${queryParams.toString()}`);

      // La respuesta ya tiene la estructura correcta según la especificación
      return response.data;
    } catch (error) {
      console.error('TracesService: Error fetching traces:', error);
      throw parseApiError(error);
    }
  }

  /**
   * Obtiene un trace específico por ID
   */
  async getTrace(
    traceId: string,
    tags?: string[]
  ): Promise<TraceResponse> {
    try {
      const params = new URLSearchParams();
      if (tags && tags.length > 0) {
        params.append('tags', tags.join(','));
      }

      const url = `/traces/${traceId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      console.log('TracesService: Fetching single trace:', url);
      
      const response = await apiClient.get(url);
      
      console.log('TracesService: Single trace response:', response.data);
      
      // El API devuelve directamente el objeto trace, no envuelto en {data: ...}
      // Necesitamos envolver la respuesta para que coincida con TraceResponse
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('TracesService: Error fetching single trace:', error);
      throw parseApiError(error);
    }
  }

  /**
   * Obtiene métricas agregadas de traces para un cliente
   */
  async getTraceMetrics(
    token: string,
    clientProjectId: string,
    params?: {
      fromTimestamp?: string;
      toTimestamp?: string;
      environment?: string[];
      tags?: string[];
    }
  ): Promise<TraceAggregatedMetrics> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('client_project_id', clientProjectId);
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              queryParams.append(key, value.join(','));
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }

      // TODO: Re-enable when backend implements /traces/metrics endpoint
      console.log('Traces metrics fetch disabled - waiting for backend endpoint');
      
      // Return mock data structure to prevent frontend errors
      return {
        totalTraces: 0,
        successRate: 0,
        averageLatency: 0,
        totalCost: 0,
        period: { start: new Date(), end: new Date() }
      };
      
      // const response = await apiClient.get(
      //   `/traces/metrics?${queryParams.toString()}`,
      //   { headers: this.getAuthHeaders(token) }
      // );
      // return response.data.data || response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  }

  /**
   * Busca traces por texto en contenido
   */
  async searchTraces(
    searchQuery: string,
    params: Omit<TraceListParams, 'name'> = {}
  ): Promise<TraceListResponse> {
    try {
      return await this.listTraces({
        ...params,
        name: searchQuery
      });
    } catch (error) {
      throw parseApiError(error);
    }
  }

  /**
   * Obtiene traces por sesión específica
   */
  async getTracesBySession(
    sessionId: string,
    tags?: string[]
  ): Promise<TraceListResponse> {
    try {
      const params: TraceListParams = {
        sessionId,
        limit: 100, // Aumentar límite para conversación completa
        tags
      };

      return await this.listTraces(params);
    } catch (error) {
      throw parseApiError(error);
    }
  }

  /**
   * Obtiene traces por usuario específico
   */
  async getTracesByUser(
    userId: string,
    tags?: string[],
    dateRange?: { from: string; to: string }
  ): Promise<TraceListResponse> {
    try {
      const params: TraceListParams = {
        userId,
        tags
      };

      if (dateRange) {
        params.fromTimestamp = dateRange.from;
        params.toTimestamp = dateRange.to;
      }

      return await this.listTraces(params);
    } catch (error) {
      throw parseApiError(error);
    }
  }

  /**
   * Obtiene estadísticas rápidas para dashboard
   */
  async getDashboardStats(
    token: string,
    clientProjectId: string
  ): Promise<{
    totalTraces: number;
    tracesToday: number;
    averageLatency: number;
    totalCost: number;
    successRate: number;
  }> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const response = await this.getTraceMetrics(token, clientProjectId, {
        fromTimestamp: today.toISOString()
      });

      return {
        totalTraces: response.totalTraces,
        tracesToday: response.tracesToday,
        averageLatency: response.averageLatency,
        totalCost: response.totalCost,
        successRate: response.successRate
      };
    } catch (error) {
      throw parseApiError(error);
    }
  }
}

export const tracesApiService = new TracesApiService();